import React, { useState, useEffect } from 'react';
import { Plus, Minus, Calculator, Info, PlusCircle, Check, AlertCircle } from 'lucide-react';
import AddonSelectorDialog from './AddonSelectorDialog';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';

const API_BASE = API_BASE_URL;

interface AddOnService {
  id: number;
  name: string;
  description: string;
  price: number;
  pricingType: 'PerPerson' | 'PerGroup';
  categoryId: number;
  categoryName: string;
  isActive: boolean;
  isOptionalChoice?: boolean; // If true, sum of all optional choice addons must equal participant count
}

// Types matching backend DTOs
interface CalculatedAddonDto {
  addOnServiceId: number;
  addOnServiceName: string;
  pricingType: string;
  quantityTotal: number;
  quantityFree: number;
  quantityPaid: number;
  pricePerUnit: number;
  subtotal: number;
  isManuallyAdjusted: boolean;
  priceOverride?: number; // For manual price adjustment
  originalIncludedQuantity?: number; // Store the original free quantity from tour config
  originalPricingType?: string; // Store the original pricing type from tour config
  isOptionalChoice?: boolean; // If true, this is an optional choice addon (e.g., meal selection)
  categoryId?: number; // Category for grouping optional choice addons
}

interface AddonAdjustment {
  addOnServiceId: number;
  addOnServiceName: string;
  pricingType: string;
  quantityTotal: number;
  pricePerUnit: number;
}

interface AddonSelectorProps {
  tourType: string; // STANDARD, PREMIUM, PRIVAT
  participantCount: number;
  onAddonsChange: (addons: CalculatedAddonDto[]) => void;
  initialAddons?: CalculatedAddonDto[];
}

const AddonSelector: React.FC<AddonSelectorProps> = ({
  tourType,
  participantCount,
  onAddonsChange,
  initialAddons = []
}) => {
  const [calculatedAddons, setCalculatedAddons] = useState<CalculatedAddonDto[]>(initialAddons);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showAddonDialog, setShowAddonDialog] = useState(false);

  // Format currency helper
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Load auto-calculated addons when component mounts or tourType/participants change
  useEffect(() => {
    loadCalculatedAddons();
  }, [tourType, participantCount]);

  const loadCalculatedAddons = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`[AddonSelector] Loading addons for ${tourType} with ${participantCount} participants`);

      const response = await fetch(
        `${API_BASE}/tour-types/${tourType}/addons/calculate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tourType,
            participantCount
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to calculate addons: ${response.statusText}`);
      }

      const addons: CalculatedAddonDto[] = await response.json();
      console.log('[AddonSelector] Loaded calculated addons:', addons);

      // Store the original included quantity and pricing type for each addon
      const addonsWithOriginal = addons.map(addon => {
        console.log(`[AddonSelector] Addon ${addon.addOnServiceName}: pricingType from backend = ${addon.pricingType}`);
        return {
          ...addon,
          originalIncludedQuantity: addon.quantityFree,
          originalPricingType: addon.pricingType
        };
      });

      setCalculatedAddons(addonsWithOriginal);
      onAddonsChange(addonsWithOriginal);
    } catch (err: any) {
      console.error('[AddonSelector] Error loading addons:', err);
      setError(err.message || 'Fehler beim Laden der Zusatzoptionen');
      setCalculatedAddons([]);
      onAddonsChange([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to recalculate free/paid split
  const recalculateAddonQuantities = (addon: CalculatedAddonDto, newQuantity: number, effectivePrice: number) => {
    // Get the original included quantity from tour configuration (stored when addon was loaded)
    const originalIncludedQuantity = addon.originalIncludedQuantity ?? 0;

    // Calculate free/paid split:
    // - Free units: up to the original included quantity from tour config
    // - Paid units: everything above the included quantity
    // Example: If included=10 and user selects 15, then free=10, paid=5
    const quantityFree = Math.min(newQuantity, originalIncludedQuantity);
    const quantityPaid = Math.max(0, newQuantity - originalIncludedQuantity);
    const subtotal = quantityPaid * effectivePrice;

    console.log('[AddonSelector] Recalculating:', {
      addonName: addon.addOnServiceName,
      newQuantity,
      originalIncludedQuantity,
      quantityFree,
      quantityPaid,
      effectivePrice,
      subtotal
    });

    return {
      quantityFree,
      quantityPaid,
      subtotal
    };
  };

  const handleQuantityChange = (addon: CalculatedAddonDto, delta: number) => {
    const newQuantity = addon.quantityTotal + delta;
    if (newQuantity < 0) return;

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, newQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: newQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handleQuantityInputChange = (addon: CalculatedAddonDto, value: string) => {
    const newQuantity = parseInt(value) || 0;
    if (newQuantity < 0) return;

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, newQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: newQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handlePriceChange = (addon: CalculatedAddonDto, newPrice: number) => {
    if (newPrice < 0) return;

    // Recalculate subtotal with new price
    const subtotal = addon.quantityPaid * newPrice;

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      priceOverride: newPrice,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handlePricingTypeChange = (addon: CalculatedAddonDto, newType: string) => {
    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      pricingType: newType,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handleRemoveAddon = (addonId: number) => {
    const updatedAddons = calculatedAddons.filter(a => a.addOnServiceId !== addonId);
    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handleAddAddon = (addon: AddOnService) => {
    // Create a new calculated addon from the selected service
    const newAddon: CalculatedAddonDto = {
      addOnServiceId: addon.id,
      addOnServiceName: addon.name,
      pricingType: addon.pricingType,
      quantityTotal: 0, // Start with 0, user can increase
      quantityFree: 0,
      quantityPaid: 0,
      pricePerUnit: addon.price,
      subtotal: 0,
      isManuallyAdjusted: true, // Mark as manually added
      priceOverride: undefined,
      originalIncludedQuantity: 0, // Manually added addons have no included quantity
      originalPricingType: addon.pricingType // Store original pricing type
    };

    const updatedAddons = [...calculatedAddons, newAddon];
    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const calculateTotalPrice = (): number => {
    return calculatedAddons.reduce((sum, addon) => sum + addon.subtotal, 0);
  };

  // Separate optional choice addons from regular addons
  const optionalChoiceAddons = calculatedAddons.filter(a => a.isOptionalChoice);
  const regularAddons = calculatedAddons.filter(a => !a.isOptionalChoice);

  // Calculate total for optional choice addons
  const getOptionalChoiceTotal = (): number => {
    return optionalChoiceAddons.reduce((sum, addon) => sum + addon.quantityTotal, 0);
  };

  // Check if optional choice validation passes
  const isOptionalChoiceValid = (): boolean => {
    if (optionalChoiceAddons.length === 0) return true;
    return getOptionalChoiceTotal() === participantCount;
  };

  // Get max quantity for a specific optional choice addon
  const getMaxForOptionalAddon = (addon: CalculatedAddonDto): number => {
    const currentTotal = getOptionalChoiceTotal();
    const remaining = participantCount - currentTotal;
    return addon.quantityTotal + remaining;
  };

  // Handle quantity change for optional choice addons with limit
  const handleOptionalChoiceQuantityChange = (addon: CalculatedAddonDto, delta: number) => {
    const newQuantity = addon.quantityTotal + delta;
    if (newQuantity < 0) return;

    // Check if we would exceed participant count
    const currentTotal = getOptionalChoiceTotal();
    const newTotal = currentTotal - addon.quantityTotal + newQuantity;
    if (newTotal > participantCount) return;

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, newQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: newQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  // Handle input change for optional choice addons with limit
  const handleOptionalChoiceInputChange = (addon: CalculatedAddonDto, value: string) => {
    const newQuantity = parseInt(value) || 0;
    if (newQuantity < 0) return;

    // Calculate max allowed for this addon
    const maxAllowed = getMaxForOptionalAddon(addon);
    const clampedQuantity = Math.min(Math.max(0, newQuantity), maxAllowed);

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, clampedQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: clampedQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const getTotalQuantityInfo = (): string => {
    const totalFree = calculatedAddons.reduce((sum, a) => sum + a.quantityFree, 0);
    const totalPaid = calculatedAddons.reduce((sum, a) => sum + a.quantityPaid, 0);

    if (totalFree > 0 && totalPaid > 0) {
      return `${totalFree} kostenlos, ${totalPaid} kostenpflichtig`;
    } else if (totalFree > 0) {
      return `${totalFree} kostenlos`;
    } else if (totalPaid > 0) {
      return `${totalPaid} kostenpflichtig`;
    }
    return 'Keine Addons';
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
        <p>Lade Zusatzoptionen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '1rem',
        background: '#fee',
        border: '1px solid #fcc',
        borderRadius: '8px',
        color: '#c00'
      }}>
        <strong>Fehler:</strong> {error}
        <button
          onClick={loadCalculatedAddons}
          style={{
            marginLeft: '1rem',
            padding: '0.5rem 1rem',
            background: '#4a9eff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="addon-selector">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div>
          <h3 style={{ margin: '0 0 0.5rem 0' }}>
            Zusatzoptionen für {tourType}
          </h3>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            {participantCount} Teilnehmer · {getTotalQuantityInfo()}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setShowAddonDialog(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#4a9eff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <PlusCircle size={18} />
            Weitere Zusatzoption
          </button>
          <button
            type="button"
            onClick={() => setShowCalculator(!showCalculator)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            <Calculator size={18} />
            {showCalculator ? 'Details ausblenden' : 'Details anzeigen'}
          </button>
        </div>
      </div>

      {/* Optional Choice Addons (Grillgericht-Auswahl etc.) */}
      {optionalChoiceAddons.length > 0 && (
        <div style={{
          background: '#f0fdf4',
          border: '2px solid #16a34a',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🍽️</span>
            <h3 style={{ margin: 0, color: '#15803d' }}>Verpflegungsauswahl</h3>
          </div>
          <p style={{ color: '#166534', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Bitte wählen Sie für jeden Teilnehmer eine Option. Die Summe muss {participantCount} ergeben.
          </p>

          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {optionalChoiceAddons.map(addon => {
              const maxForThisAddon = getMaxForOptionalAddon(addon);
              const optionalTotal = getOptionalChoiceTotal();

              return (
                <div
                  key={addon.addOnServiceId}
                  style={{
                    background: 'white',
                    border: '1px solid #86efac',
                    borderRadius: '8px',
                    padding: '1rem'
                  }}
                >
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#1a2332' }}>
                    {addon.addOnServiceName}
                  </h4>
                  <p style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '0.75rem' }}>
                    {formatCurrency(addon.priceOverride ?? addon.pricePerUnit)} pro Person
                  </p>

                  {/* Quantity Controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleOptionalChoiceQuantityChange(addon, -1)}
                      disabled={addon.quantityTotal === 0}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: addon.quantityTotal === 0 ? '#f0f0f0' : '#dcfce7',
                        cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                        opacity: addon.quantityTotal === 0 ? 0.5 : 1
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={addon.quantityTotal}
                      onChange={(e) => handleOptionalChoiceInputChange(addon, e.target.value)}
                      min="0"
                      max={maxForThisAddon}
                      style={{
                        width: '5rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        padding: '0.5rem',
                        border: '2px solid #16a34a',
                        borderRadius: '6px'
                      }}
                    />
                    <button
                      onClick={() => handleOptionalChoiceQuantityChange(addon, 1)}
                      disabled={optionalTotal >= participantCount}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #16a34a',
                        borderRadius: '4px',
                        background: optionalTotal >= participantCount ? '#f0f0f0' : '#16a34a',
                        color: optionalTotal >= participantCount ? '#999' : 'white',
                        cursor: optionalTotal >= participantCount ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Validation Status */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: isOptionalChoiceValid() ? '#dcfce7' : '#fef9c3',
            border: isOptionalChoiceValid() ? '1px solid #16a34a' : '1px solid #facc15'
          }}>
            {isOptionalChoiceValid() ? (
              <>
                <Check size={20} style={{ color: '#16a34a' }} />
                <span style={{ color: '#15803d', fontWeight: 'bold' }}>
                  ✓ Alle {participantCount} Teilnehmer haben eine Auswahl ({getOptionalChoiceTotal()}/{participantCount})
                </span>
              </>
            ) : (
              <>
                <AlertCircle size={20} style={{ color: '#ca8a04' }} />
                <span style={{ color: '#854d0e', fontWeight: 'bold' }}>
                  Noch {participantCount - getOptionalChoiceTotal()} Auswahl(en) offen ({getOptionalChoiceTotal()}/{participantCount})
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Regular Addons */}
      {calculatedAddons.length === 0 ? (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: '#f8f9fa',
          borderRadius: '8px',
          color: '#666'
        }}>
          <Info size={48} style={{ margin: '0 auto 1rem' }} />
          <p>Keine automatischen Zusatzoptionen für diesen Tour-Typ konfiguriert.</p>
        </div>
      ) : regularAddons.length > 0 ? (
        <div>
          <h3 style={{ marginBottom: '1rem' }}>Weitere Zusatzoptionen</h3>
          <div style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))'
          }}>
            {regularAddons.map(addon => (
              <div
                key={addon.addOnServiceId}
                style={{
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: addon.subtotal > 0 ? '#f0f8ff' : 'white',
                  transition: 'all 0.2s'
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#1a2332' }}>
                      {addon.addOnServiceName}
                    </h4>
                    {/* Pricing Type Selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.85rem', color: '#666', minWidth: '60px' }}>
                        Gültigkeit:
                      </label>
                      <select
                        value={addon.pricingType}
                        onChange={(e) => handlePricingTypeChange(addon, e.target.value)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="PerPerson">Pro Person</option>
                        <option value="PerGroup">Pro Gruppe</option>
                        <option value="Fixed">Festpreis</option>
                        <option value="PerNight">Pro Nacht</option>
                        <option value="PerHour">Pro Stunde</option>
                        <option value="PerDay">Pro Tag</option>
                      </select>
                    </div>
                  </div>
                  {addon.isManuallyAdjusted && (
                    <span style={{
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      background: '#fef3c7',
                      color: '#92400e',
                      borderRadius: '4px'
                    }}>
                      Angepasst
                    </span>
                  )}
                </div>

                {/* Free Quantity Badge - Always visible */}
                {addon.quantityFree > 0 && (
                  <div style={{
                    background: '#dcfce7',
                    border: '1px solid #16a34a',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    marginBottom: '0.75rem',
                    fontSize: '0.85rem',
                    color: '#15803d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    <strong>✓ {addon.quantityFree} Einheit{addon.quantityFree !== 1 ? 'en' : ''} inklusive</strong>
                    <span style={{ color: '#16a34a' }}>(keine Berechnung)</span>
                  </div>
                )}

                {/* Quantity Breakdown - Detailed view */}
                {showCalculator && (
                  <div style={{
                    background: '#f8f9fa',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    marginBottom: '0.75rem',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span>Gesamt:</span>
                      <strong>{addon.quantityTotal}</strong>
                    </div>
                    {addon.quantityFree > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', color: '#16a34a' }}>
                        <span>Inklusiv:</span>
                        <span>-{addon.quantityFree}</span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid #ddd',
                      color: addon.quantityPaid > 0 ? '#dc2626' : '#666'
                    }}>
                      <span>Kostenpflichtig:</span>
                      <strong>{addon.quantityPaid}</strong>
                    </div>
                  </div>
                )}

                {/* Quantity Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleQuantityChange(addon, -1)}
                      disabled={addon.quantityTotal === 0}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                        opacity: addon.quantityTotal === 0 ? 0.5 : 1
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={addon.quantityTotal}
                      onChange={(e) => handleQuantityInputChange(addon, e.target.value)}
                      min="0"
                      style={{
                        width: '4rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <button
                      onClick={() => handleQuantityChange(addon, 1)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #4a9eff',
                        borderRadius: '4px',
                        background: '#4a9eff',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {addon.quantityTotal > 0 && (
                    <button
                      onClick={() => handleRemoveAddon(addon.addOnServiceId)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: '#fee',
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        color: '#c00',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Entfernen
                    </button>
                  )}
                </div>

                {/* Price Configuration */}
                <div style={{
                  padding: '0.75rem',
                  background: addon.subtotal > 0 ? '#f0fdf4' : '#f8f9fa',
                  borderRadius: '6px'
                }}>
                  {/* Price per unit - editable */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <label style={{ fontSize: '0.85rem', color: '#666', minWidth: '80px' }}>
                      Preis/Einheit:
                    </label>
                    <input
                      type="number"
                      value={addon.priceOverride ?? addon.pricePerUnit}
                      onChange={(e) => handlePriceChange(addon, parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      style={{
                        padding: '0.25rem 0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        width: '100px',
                        background: addon.priceOverride ? '#fef3c7' : 'white'
                      }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>€</span>
                    {addon.priceOverride && (
                      <span style={{ fontSize: '0.75rem', color: '#92400e' }}>
                        (Original: {formatCurrency(addon.pricePerUnit)})
                      </span>
                    )}
                  </div>

                  {/* Total calculation */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #ddd'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      {addon.quantityPaid > 0 ? (
                        <>
                          {addon.quantityPaid}× {formatCurrency(addon.priceOverride ?? addon.pricePerUnit)}
                          {addon.quantityFree > 0 && (
                            <div style={{ fontSize: '0.8rem', color: '#16a34a', marginTop: '0.25rem' }}>
                              ({addon.quantityFree}× kostenlos)
                            </div>
                          )}
                        </>
                      ) : (
                        <span style={{ color: '#16a34a', fontWeight: 'bold' }}>Komplett kostenlos</span>
                      )}
                    </div>
                    <strong style={{
                      fontSize: '1.1rem',
                      color: addon.subtotal > 0 ? '#16a34a' : '#16a34a'
                    }}>
                      {formatCurrency(addon.subtotal)}
                    </strong>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          {regularAddons.length > 0 && (
            <div style={{
              marginTop: '1.5rem',
              padding: '1.25rem',
              background: '#f0fdf4',
              border: '2px solid #16a34a',
              borderRadius: '8px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{ margin: '0 0 0.25rem 0', color: '#1a2332' }}>
                    Zusatzoptionen Gesamt
                  </h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                    {calculatedAddons.filter(a => a.quantityTotal > 0).length} Addon(s) ausgewählt
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#16a34a'
                  }}>
                    {formatCurrency(calculateTotalPrice())}
                  </div>
                  {showCalculator && (
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                      {calculatedAddons.reduce((sum, a) => sum + a.quantityPaid, 0)} kostenpflichtige Einheiten
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Addon Selector Dialog */}
      <AddonSelectorDialog
        isOpen={showAddonDialog}
        onClose={() => setShowAddonDialog(false)}
        onSelect={handleAddAddon}
        excludeIds={calculatedAddons.map(a => a.addOnServiceId)}
      />

      <style>{`
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #4a9eff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AddonSelector;
